import { useState, FC, useRef, RefObject, useEffect, useMemo } from 'react';
import { Button, TextField, Grid, FormControl, InputLabel, Select, MenuItem, Dialog, DialogContent, DialogTitle, DialogActions, Paper, IconButton, Box, Alert, AlertTitle } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { PaperProps } from '@mui/material/Paper';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Draggable from 'react-draggable';
import CloseIcon from '@mui/icons-material/Close';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { normalizeString } from 'utils/Validations';
import { TimekeeperService } from 'services/TimekeeperService';
import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import 'dayjs/locale/es';
import 'dayjs/locale/en';

dayjs.extend(localizedFormat);

enum TipoDateEnum {
    FECHA_ESTABLECIMIENTO = 'FECHA_ESTABLECIMIENTO',
    FECHA_ESPECIFICA_PERSONA = 'FECHA_ESPECIFICA_PERSONA',
}

// Schema de validación Zod
const createUpdateAccrualDateSchema = (t: any, rows: any[], currentId?: number) => {
    return z
        .object({
            name: z
                .string()
                .transform((val) => val.trim())
                .pipe(
                    z
                        .string()
                        .min(2, { message: t('general.zod.string.min_chars', { min: 2 }) })
                        .max(100, { message: t('general.zod.string.max_chars', { max: 100 }) })
                        .refine((val) => /^[a-zA-Z0-9\sáéíóúÁÉÍÓÚñÑüÜ\-_]+$/.test(val), { message: t('general.zod.string.invalid_characters') })
                        .refine((val) => !rows.some((row: any) => normalizeString(row.name) === normalizeString(val) && row.accrual_date_id !== currentId), {
                            message: t('accruals.accrual_dates.dialog.alerts.name_exist'),
                        }),
                ),
            type_date: z.nativeEnum(TipoDateEnum),
            date: z.custom<Dayjs | null>(),
            date_person_id: z.number().nullable(),
        })
        .superRefine((data, ctx) => {
            // Validar fecha requerida para tipo establecimiento
            if (data.type_date === TipoDateEnum.FECHA_ESTABLECIMIENTO && !data.date) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: t('general.zod.date.required'),
                    path: ['date'],
                });
            }
            // Validar date_person_id requerido para tipo persona
            if (data.type_date === TipoDateEnum.FECHA_ESPECIFICA_PERSONA && data.date_person_id === null) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: t('general.zod.string.required'),
                    path: ['date_person_id'],
                });
            }
        });
};

interface UpdateProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    rows: any;
    rowToEdit: any;
    reloading: () => void;
    cleanSelected: () => void;
}

function PaperComponent(props: PaperProps) {
    const nodeRef = useRef<HTMLDivElement>(null);
    return (
        <Draggable nodeRef={nodeRef as RefObject<HTMLDivElement>} handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
}

const types = {
    'Fecha de establecimiento': TipoDateEnum.FECHA_ESTABLECIMIENTO,
    'Fecha específica de persona': TipoDateEnum.FECHA_ESPECIFICA_PERSONA,
    'Establishment Date': TipoDateEnum.FECHA_ESTABLECIMIENTO,
    'Specific Date Person': TipoDateEnum.FECHA_ESPECIFICA_PERSONA,
};

const timekeeperService = new TimekeeperService();

const UpdateAccrualDate: FC<UpdateProps> = ({ open, setOpen, rows, rowToEdit, reloading, cleanSelected }) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const [availablePersonDates, setAvailablePersonDates] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [alerts, setAlerts] = useState(false);

    // Schema con validaciones dinámicas
    const schema = useMemo(() => createUpdateAccrualDateSchema(t, rows, rowToEdit?.accrual_date_id), [t, rows, rowToEdit?.accrual_date_id]);

    type FormValues = z.infer<typeof schema>;

    const defaultValues: FormValues = useMemo(
        () => ({
            name: '',
            type_date: TipoDateEnum.FECHA_ESTABLECIMIENTO,
            date: null,
            date_person_id: null,
        }),
        [],
    );

    const {
        control,
        register,
        reset,
        handleSubmit,
        watch,
        formState: { errors, isDirty },
    } = useForm<FormValues>({
        defaultValues,
        resolver: zodResolver(schema),
        mode: 'onChange',
    });

    const type_date = watch('type_date');

    useEffect(() => {
        const loadData = async () => {
            const response = await timekeeperService.accrual_date.getAll({
                type_date__eq: TipoDateEnum.FECHA_ESPECIFICA_PERSONA,
            });
            setAvailablePersonDates(response.length > 0 ? response : []);
        };

        if (open && rowToEdit) {
            loadData();

            // Cargar datos en el formulario
            reset({
                name: rowToEdit.name || '',
                type_date: types[rowToEdit.type_date as keyof typeof types],
                date: rowToEdit.date ? dayjs(rowToEdit.date) : null,
                date_person_id: rowToEdit.date_person_id ?? null,
            });
        }
    }, [open, rowToEdit, reset]);

    const closeForm = () => {
        reset(defaultValues);
        setOpen(false);
        setAlerts(false);
        setLoading(false);
        cleanSelected();
    };

    const changes = () => {
        if (isDirty) {
            setAlerts(true);
        } else {
            closeForm();
        }
    };

    const onSubmit = async (data: FormValues) => {
        setLoading(true);

        const dataToUpdate = {
            name: data.name,
            date: data.type_date === TipoDateEnum.FECHA_ESTABLECIMIENTO ? (data.date ? data.date.toDate() : null) : null,
            type_date: data.type_date,
            date_person_id: data.type_date === TipoDateEnum.FECHA_ESPECIFICA_PERSONA ? data.date_person_id : null,
            accrual_date_id: rowToEdit.accrual_date_id ? rowToEdit.accrual_date_id : null,
        };

        try {
            await timekeeperService.accrual_date.update(rowToEdit.id, dataToUpdate);

            enqueueSnackbar(t('accruals.accrual_dates.dialog.alerts.update.success'), { variant: 'success' });
            closeForm();
            reloading();
        } catch (error) {
            console.error(error);
            enqueueSnackbar(t('accruals.accrual_dates.dialog.alerts.update.error'), { variant: 'error' });
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    return;
                }
                changes();
            }}
            disableRestoreFocus
            fullWidth
            maxWidth="sm"
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                {t('accruals.accrual_dates.dialog.titles.edit')}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={() => changes()}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <Box sx={{ top: 0, zIndex: 10, pb: 1 }}>
                {alerts && (
                    <Alert
                        variant="filled"
                        severity="warning"
                        sx={{ mt: 2 }}
                        action={
                            <>
                                <Button onClick={() => setAlerts(false)} color="inherit" size="small" variant="text" disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.yes')}
                                </Button>

                                <Button onClick={() => closeForm()} color="inherit" size="small" variant="text" disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.no')}
                                </Button>
                            </>
                        }
                    >
                        <AlertTitle>{t('general.alerts.unsaved_changes.message')}</AlertTitle>
                    </Alert>
                )}
            </Box>
            <DialogContent dividers>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            label={t('accruals.accrual_dates.dialog.inputs.name')}
                            autoComplete="off"
                            {...register('name')}
                            error={!!errors.name}
                            helperText={errors.name?.message}
                            slotProps={{
                                htmlInput: { maxLength: 100 },
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <Controller
                            name="type_date"
                            control={control}
                            render={({ field }) => (
                                <FormControl fullWidth>
                                    <InputLabel>{t('accruals.accrual_dates.dialog.inputs.type_date')}</InputLabel>
                                    <Select {...field} label={t('accruals.accrual_dates.dialog.inputs.type_date')}>
                                        <MenuItem value={TipoDateEnum.FECHA_ESTABLECIMIENTO}>{t('accruals.accrual_dates.dialog.sections.establishment_dates')}</MenuItem>
                                        <MenuItem value={TipoDateEnum.FECHA_ESPECIFICA_PERSONA}>{t('accruals.accrual_dates.dialog.sections.person_specific_dates')}</MenuItem>
                                    </Select>
                                </FormControl>
                            )}
                        />
                    </Grid>

                    {type_date === TipoDateEnum.FECHA_ESTABLECIMIENTO && (
                        <Grid size={{ xs: 12 }}>
                            <Controller
                                name="date"
                                control={control}
                                render={({ field }) => (
                                    <DatePicker
                                        label={t('accruals.accrual_dates.dialog.inputs.date')}
                                        value={field.value}
                                        onChange={field.onChange}
                                        format="L"
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                error: !!errors.date,
                                                helperText: errors.date?.message,
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                    )}

                    {type_date === TipoDateEnum.FECHA_ESPECIFICA_PERSONA && (
                        <Grid size={{ xs: 12 }}>
                            <Controller
                                name="date_person_id"
                                control={control}
                                render={({ field }) => (
                                    <FormControl fullWidth error={!!errors.date_person_id}>
                                        <InputLabel>{t('accruals.accrual_dates.dialog.inputs.date')}</InputLabel>
                                        <Select
                                            {...field}
                                            value={field.value ?? ''}
                                            label={t('accruals.accrual_dates.dialog.inputs.date')}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                field.onChange(Number(value));
                                            }}
                                        >
                                            <MenuItem value={0}>{t('accruals.accrual_dates.dialog.inputs.types_date_selectable.hire_date')}</MenuItem>
                                            {availablePersonDates.map((dateOption) => (
                                                <MenuItem key={dateOption.accrual_date_id} value={dateOption.accrual_date_id}>
                                                    {dateOption.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            />
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button variant="contained" color="error" onClick={() => changes()}>
                    {t('general.buttons.cancel')}
                </Button>
                <Button variant="contained" color="primary" onClick={handleSubmit(onSubmit)} disabled={loading || !isDirty} loading={loading}>
                    {t('general.buttons.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export { UpdateAccrualDate };
